import { useTranslation } from 'react-i18next';
import { CheckIcon } from 'lucide-react';
import type { LegacyOrder } from '@/entities/legacy-order';
import { LegacyCheckAccess, useLegacyAccess } from '@/entities/legacy-access';
import { LegacyReOrderIcon, LegacyCopyIcon, LegacyCancelIcon } from './legacy-icons';

/**
 * Row actions: settle courier debt · re-dispatch courier · duplicate · cancel.
 *
 * The enablement rules are transcribed from the old
 * `ActionCellRenderer`, including which stages each action accepts:
 *
 * - **re-order** — the original's CLICK guard accepted stages `[22, 27]`
 *   (cancelled, courier-not-found) while its DISABLED-TINT check used
 *   `[22, 23, 27]`. So on a stage-23 order the icon rendered enabled but the
 *   click did nothing. That is a bug in the source, not a subtlety worth
 *   reproducing: a control that looks clickable and silently isn't is the kind
 *   of thing this port should fix. Both the tint and the guard use the click
 *   guard's `[22, 27]` here, so the icon is greyed out exactly when it is
 *   inert. Flagged for you rather than silently kept.
 * - **cancel** — stages 1–14, i.e. anything not yet delivered/returned/cancelled.
 * - **duplicate** — always available.
 *
 * Permission gating replays the source's own split: re-order and cancel keep
 * their icons rendered but greyed and inert without `delivery.reorder` /
 * `delivery.cancel` (the old `check()` tinted the icon and swallowed the
 * click), while duplicate HIDES without `delivery.create` (the old copy icon
 * sat inside a `CheckAccess`). Settle stays ungated — the source put no
 * permission check on it either.
 */

/**
 * Courier payment state meaning "the courier owes the merchant for goods they
 * fronted" — the only state from which the debt can be settled.
 */
const COURIER_DEBT_STATUS = 6;

/**
 * Stages at which that debt may be settled. The source's own pair
 * (`ActionCellRenderer.tsx:16`): the delivery is finished (15) or returned
 * (20), so the goods have changed hands and the money is now owed.
 */
const SETTLE_DEBT_STAGES = [15, 20];

/** Stages where the courier search may be re-dispatched. */
const REORDER_STAGES = [22, 27];

/** Stages where an order may still be cancelled (pre-delivery). */
const CANCEL_STAGES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

interface LegacyActionsCellProps {
  order: LegacyOrder;
  onReorder: (order: LegacyOrder) => void;
  onCancel: (order: LegacyOrder) => void;
  onDuplicate: (order: LegacyOrder) => void;
  /** Settles a courier's product debt — see `COURIER_DEBT_STATUS`. */
  onSettleDebt: (order: LegacyOrder) => void;
}

export function LegacyActionsCell({
  order,
  onReorder,
  onCancel,
  onDuplicate,
  onSettleDebt,
}: LegacyActionsCellProps) {
  const { t } = useTranslation('legacy');
  const check = useLegacyAccess();

  /*
   * Unlike the other three this action is CONDITIONALLY RENDERED, not merely
   * disabled: the source shows the tick only on an order that actually carries
   * a debt, and a permanently-greyed tick on every other row would suggest a
   * settlement is possible where there is nothing to settle.
   */
  const canSettleDebt =
    order.courier_payment_status === COURIER_DEBT_STATUS &&
    SETTLE_DEBT_STAGES.includes(order.stage);

  const canReorder = REORDER_STAGES.includes(order.stage) && check('delivery.reorder');
  const canCancel = CANCEL_STAGES.includes(order.stage) && check('delivery.cancel');

  /** Actions must never fall through to the row's open-drawer handler. */
  const run = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  /**
   * Row hover darkens ENABLED icons to black and marks disabled ones
   * `cursor: not-allowed` — the old grid's `is_row_hover` CSS
   * (`AgGridTable.tsx:270-300`), reproduced with `group-hover` off the row's
   * `group` class.
   */
  const tint = (enabled: boolean) =>
    enabled
      ? 'text-[var(--lg-grey-600)] cursor-pointer group-hover:text-[var(--lg-fg)]'
      : 'text-[var(--lg-grey-400)] cursor-not-allowed';

  return (
    <div className="flex items-center gap-3">
      {canSettleDebt && (
        <button
          type="button"
          onClick={run(() => onSettleDebt(order))}
          title={t('actions.settle_debt')}
          aria-label={t('actions.settle_debt')}
          className={tint(true)}
        >
          <CheckIcon className="size-5" />
        </button>
      )}

      <button
        type="button"
        disabled={!canReorder}
        onClick={run(() => onReorder(order))}
        title={t('actions.reorder')}
        aria-label={t('actions.reorder')}
        className={tint(canReorder)}
      >
        <LegacyReOrderIcon />
      </button>

      <LegacyCheckAccess permission="delivery.create">
        <button
          type="button"
          onClick={run(() => onDuplicate(order))}
          title={t('actions.copy')}
          aria-label={t('actions.copy')}
          className={tint(true)}
        >
          <LegacyCopyIcon />
        </button>
      </LegacyCheckAccess>

      <button
        type="button"
        disabled={!canCancel}
        onClick={run(() => onCancel(order))}
        title={t('actions.cancel')}
        aria-label={t('actions.cancel')}
        className={tint(canCancel)}
      >
        <LegacyCancelIcon className="size-8" />
      </button>
    </div>
  );
}
